import { Stack } from '@mui/material';
import { Typography } from '../Typography/Typography';
import {
    modalHeaderClassName,
    ModalSizingVariant,
    paddingValues,
} from './Modal.styleUtils';
import {
    DesignSystemIconSizing,
    DesignSystemTypographyVariants,
} from '@upheal/design-system/theme/design-system/types';
import { ReactNode } from 'react';
import { RemixiconComponentType } from '@remixicon/react';
import { FeatureIconBaseProps } from '../FeatureIcon/FeatureIcon';
import { LeadItem } from '../LeadItem/LeadItem';
import { useSlotContext } from '@upheal/design-system/utils/useSlotContext';
import { closeButtonSizeValues, ModalCloseButton } from './ModalCloseButton';

const titleSizeVariants = {
    sm: 'md',
    md: 'lg',
} as const satisfies Record<ModalSizingVariant, DesignSystemTypographyVariants>;

const iconSize = {
    sm: 'lg',
    md: 'xl',
} as const satisfies Record<ModalSizingVariant, DesignSystemIconSizing>;

export interface ModalHeaderProps {
    /**
     * Optional icon to display in the modal header, see `layout`
     */
    icon?: RemixiconComponentType;
    /**
     * Title for the modal header
     */
    title: ReactNode;
    /**
     * Optional subtitle for the modal header
     */
    subtitle?: ReactNode;
    /**
     * Determines layout for icon and title with subtitle
     * row = icon on the left, title and subtitle on the right
     * column = icon on top, title and subtitle below
     * @default 'row'
     */
    layout?: 'row' | 'column';

    slotProps?: {
        leadItem?: Omit<FeatureIconBaseProps, 'size'>;
    };

    onClose?: () => void;
}

export const ModalHeader = ({
    icon,
    title,
    subtitle,
    layout = 'row',
    slotProps,
    onClose,
}: ModalHeaderProps) => {
    const { id = 'modal', sizing = 'md' } = useSlotContext({}, 'modal');

    const isRowLayout = layout === 'row';
    const titlesAlign = isRowLayout ? 'left' : 'center';
    const gap = isRowLayout ? 2 : 1.5;

    return (
        <Stack
            direction="row"
            gap={2}
            sx={{
                ...paddingValues[sizing],
                borderBottom: '1px solid',
                borderColor: 'ds.border.neutral.secondary',
            }}
            className={modalHeaderClassName}
        >
            <Stack
                direction={layout}
                gap={gap}
                alignItems={isRowLayout ? 'flex-start' : 'center'}
                sx={{
                    flexGrow: 1,
                    width: `calc(100% - ${gap * 8}px - ${
                        closeButtonSizeValues.width ??
                        closeButtonSizeValues.height
                    })`,
                }}
            >
                {icon && (
                    <LeadItem
                        {...{ colorScheme: 'accent1', ...slotProps?.leadItem }}
                        type="feature-icon"
                        icon={icon}
                        size={iconSize[sizing]}
                    />
                )}
                <Stack sx={{ flex: '1 1 auto', maxWidth: '100%' }}>
                    <Typography
                        variant={titleSizeVariants[sizing]}
                        fontWeight="semibold"
                        color="ds.fg.neutral.primary"
                        textAlign={titlesAlign}
                        id={`${id}-title`}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="sm"
                            fontWeight="regular"
                            color="ds.fg.neutral.secondary"
                            sx={{ textAlign: titlesAlign }}
                            id={`${id}-subtitle`}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {onClose && <ModalCloseButton onClick={onClose} />}
        </Stack>
    );
};
